import React from "react";
import { ActionIcon, Box, Image, Tooltip } from "@mantine/core";
import {
  IconPencil,
  IconTrash,
  IconDroplet,
  IconTag,
  IconPackage,
} from "@tabler/icons-react";
import styles from "./LineItemRow.module.css";

interface LineItemRowProps {
  variant: "service" | "product" | "package";
  /** Product image url — product rows show a hatch placeholder without one. */
  imageUrl?: string;
  name: string;
  /** Meta items rendered icon+text on one wrapping row (time, staff, qty…). */
  meta?: React.ReactNode;
  /** Inline quantity stepper (or other control) rendered on its own line
   *  under the meta row. */
  quantityControl?: React.ReactNode;
  /** Secondary italic line (e.g. service option answers). */
  subMeta?: React.ReactNode;
  /** Lime discount chip labels, one per applied discount, e.g.
   *  ["Birthday 20%", "Loyalty $5.00"]. */
  discountChips?: string[];
  /** Original price shown struck-through when a discount applies. */
  originalPrice?: string;
  price: string;
  /** Dimmed suffix rendered after the price, e.g. "/unit" / "/syringe". */
  priceSuffix?: React.ReactNode;
  onEdit?: () => void;
  onDelete?: () => void;
  deleteDisabled?: boolean;
  deleteDisabledTooltip?: string;
  readOnly?: boolean;
  /** Extra body content under the meta/chip (e.g. Service Modifiers). */
  children?: React.ReactNode;
}

/**
 * Hairline transaction-items row per the redesign: leading tile, name +
 * meta, a discount chip per applied discount, right-aligned price with
 * edit/delete icon actions.
 */
export default function LineItemRow({
  variant,
  imageUrl,
  name,
  meta,
  quantityControl,
  subMeta,
  discountChips = [],
  originalPrice,
  price,
  priceSuffix,
  onEdit,
  onDelete,
  deleteDisabled = false,
  deleteDisabledTooltip,
  readOnly = false,
  children,
}: LineItemRowProps) {
  const deleteButton = (
    <ActionIcon
      variant="subtle"
      className={`${styles.actionIcon} ${styles.actionIconDanger}`}
      onClick={onDelete}
      disabled={deleteDisabled}
      aria-label={`Delete ${name}`}
    >
      <IconTrash size={15} />
    </ActionIcon>
  );

  return (
    <Box className={styles.row}>
      {variant === "service" ? (
        <Box className={`${styles.iconTile} ${styles.serviceTile}`}>
          <IconDroplet size={22} stroke={1.9} />
        </Box>
      ) : variant === "package" ? (
        <Box className={`${styles.iconTile} ${styles.serviceTile}`}>
          <IconPackage size={22} stroke={1.9} />
        </Box>
      ) : (
        <Box className={`${styles.iconTile} ${styles.productTile}`}>
          {imageUrl ? (
            <Image src={imageUrl} w={48} h={48} fit="contain" alt={name} />
          ) : (
            <Box className={styles.productPlaceholder} />
          )}
        </Box>
      )}

      <Box className={styles.body}>
        <Box className={styles.name}>{name}</Box>
        {meta && <Box className={styles.metaRow}>{meta}</Box>}
        {quantityControl && (
          <Box className={styles.quantityControl}>{quantityControl}</Box>
        )}
        {subMeta && <Box className={styles.subMeta}>{subMeta}</Box>}
        {discountChips.map((chip, index) => (
          <Box
            key={`${index}-${chip}`}
            component="span"
            className={styles.discountChip}
          >
            <IconTag size={12} stroke={2.2} />
            {chip}
          </Box>
        ))}
        {children}
      </Box>

      <Box className={styles.priceBlock}>
        <Box className={styles.priceRow}>
          {originalPrice && (
            <Box component="span" className={styles.strikePrice}>
              {originalPrice}
            </Box>
          )}
          <Box component="span" className={styles.price}>
            {price}
            {priceSuffix && (
              <Box component="span" className={styles.priceSuffix}>
                {priceSuffix}
              </Box>
            )}
          </Box>
        </Box>
        {!readOnly && (
          <Box className={styles.actions}>
            {onEdit && (
              <ActionIcon
                variant="subtle"
                className={styles.actionIcon}
                onClick={onEdit}
                aria-label={`Edit ${name}`}
              >
                <IconPencil size={15} />
              </ActionIcon>
            )}
            {deleteDisabled && deleteDisabledTooltip ? (
              <Tooltip label={deleteDisabledTooltip} multiline w={350}>
                {deleteButton}
              </Tooltip>
            ) : (
              deleteButton
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}
